import React, { useContext } from 'react'
import { Box, CMSContent, Divider, Text, Icon, Button } from '@deseretbook/react-ui'
import propTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useMediaQuery } from '@deseretbook/react-utils'
import PageLayout from '../layouts/DefaultLayout'
import ErrorComponent from '../components/ErrorComponent'
import LoadingIndicator from '../components/media/loadingIndicator/LoadingIndicator'
import { readEbookUrl, listenAudiobookUrl } from '../utils/routes'
import { openPopupPlayer } from '../utils/media'
import useMedia from '../hooks/useMedia'
import ActNowButton from '../components/buttons/ActNowButton'
import checkMediaSubscription from '../utils/checkMediaSub'
import useUserSub from '../hooks/useUserSub'
import { BOOKSHELF_PLUS_IDS, DIGITAL_VIP_IDS } from '../subscriptionIds'
import AddToLibrary from '../components/buttons/AddToLibrary'
import AuthorLinks from '../components/media/authorLinks/AuthorLinks'
import { SearchContext } from '../components/search/SearchProvider'

import './LandingPage.css'
import BreadcrumbButton from '../components/buttons/BreadcrumbButton'
import trimHtml from '../utils/trimHtml'
import CopyTooltip from '../components/tooltips/CopyTooltip'
import AttributesCard from '../components/media/mediaCard/AttributesCard'

/**
 * Landing page for Media items
 *
 * @param {object} props - component props
 * @returns {function} Component
 */
const LandingPage = ({ match }) => {
  const { id, sku } = match.params
  const { updateMedia } = useContext(SearchContext)
  const { isLoading, error, media, setMedia } = useMedia(id)
  const { userPlus, userVip } = useUserSub()
  const isMediumOrLarger = useMediaQuery(850)
  // Set media types
  const isEbook = media.ebook && media.ebook.sku === sku
  const isAudiobook = media.audiobook && media.audiobook.sku === sku

  // Subscription IDs
  const plusIds = BOOKSHELF_PLUS_IDS
  const vipIds = DIGITAL_VIP_IDS

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (error) {
    return (
      <PageLayout>
        <ErrorComponent error={error} />
      </PageLayout>
    )
  }

  // Collection of variables to be used in the component
  const accessor = isEbook ? 'ebook' : 'audiobook'
  const isPlus = checkMediaSubscription(media[accessor], plusIds)
  const isVip = checkMediaSubscription(media[accessor], vipIds)
  const isFree = (media[accessor].subscriptionPlanIds || []).includes('free')
  const cover = (media && media[accessor] && media[accessor].cover) || media.cover

  const { inLibrary = false, subscribed = false, purchased = false, subscribable = false } =
    media[accessor] && media[accessor].userInfo

  /**
   * Handles updating the media object returned from the API
   *
   * @param {object} newMedia - The updated media object.
   * @param {number} mediaId - The id of the media to match.
   * @param {string} mediaSku - The sku of the media to match.
   */
  const handleUpdateMedia = (newMedia, mediaId, mediaSku) => {
    const formattedMedia = Object.assign({}, newMedia)

    // Remove ebook or audiobook from media object
    if (accessor === 'ebook') {
      formattedMedia.audiobook = null
    } else {
      formattedMedia.ebook = null
    }

    updateMedia(formattedMedia, mediaId, mediaSku)
    setMedia(formattedMedia)
  }

  let url = ''

  if (isEbook) {
    url = readEbookUrl(id, sku)
  } else {
    url = listenAudiobookUrl(id, sku)
  }

  // Decide which button to render
  const buttonProps = isAudiobook
    ? {
        onClick: () => {
          openPopupPlayer(listenAudiobookUrl(id, sku))
        },
      }
    : { to: url, as: Link }

  let actionButton = null

  if (inLibrary || subscribed) {
    actionButton = (
      <ActNowButton
        purchased={purchased}
        buttonProps={buttonProps}
        isAudiobook={isAudiobook}
        isPlus={isPlus && userPlus}
        isFree={isFree}
        isVip={(isVip || isPlus) && userVip}
      />
    )
  } else if (subscribable && (isFree || (userVip && (isVip || isPlus)) || (userPlus && isPlus))) {
    actionButton = (
      <AddToLibrary
        isFree={isFree}
        isPlus={isPlus && userPlus}
        isVip={(isVip || isPlus) && userVip}
        mediaId={media.mediaId}
        sku={media[accessor] && media[accessor].sku}
        updateMedia={handleUpdateMedia}
      />
    )
  }

  const htmlDescription = media.description ? trimHtml(media.description, 10000) : ''

  const dbcomUrl = (media && media[accessor] && media[accessor].productUrl) || ''

  return (
    <PageLayout id="LandingPageContainer">
      <Box isFullwidth alignItems="center" justifyContent="start" marginTop={1}>
        <BreadcrumbButton />
      </Box>
      <Divider
        style={{ boxShadow: 'none', borderBottom: '1px solid #d7d7d7', marginBottom: '48px' }}
      />
      <Box
        alignItems={isMediumOrLarger ? 'start' : 'center'}
        justifyContent="start"
        isColumn={!isMediumOrLarger}
      >
        <Box
          marginRight={2}
          marginBottom={5}
          style={{ minWidth: '360px', height: '480px', maxWidth: '400px' }}
        >
          <figure className="image">
            <img
              src={cover && cover.fullUrl}
              alt={media.title}
              style={{ borderRadius: '20px', boxShadow: '0px 12px 12px 0px #0000001F' }}
            />
          </figure>
        </Box>
        <Box isFullwidth>
          <Box alignItems="end" justifyContent="space-between">
            <Text
              display="inline-block"
              style={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: '34px',
                lineHight: '39.84px',
              }}
            >
              {media.title}
            </Text>
            <Box alignItems="center" marginLeft={1}>
              <CopyTooltip textToCopy={dbcomUrl}>
                <Button className="share-button" style={{ border: 'none' }}>
                  <Icon name="share" className="link-icon" />
                </Button>
              </CopyTooltip>

              {actionButton}
            </Box>
          </Box>

          <AuthorLinks authors={media.authors} />
          <div
            style={{
              marginTop: '16px',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '22px',
              color: '#000000A6',
            }}
          >
            <CMSContent
              dangerouslySetInnerHTML={{ __html: htmlDescription }}
              itsNotDangerousBecause="mediaInfo.description comes directly from the Bookshelf API, and should be trustworthy"
            />
          </div>
          <br />
          <Box alignItems="center" justifyContent="start" style={{ marginBottom: '0.5rem' }}>
            {isEbook && (
              <Icon
                name="ebook"
                style={{
                  borderRadius: '1rem',
                  backgroundColor: '#B6BFBF26',
                  padding: '0.25rem',
                  width: '36px',
                  height: '36px',
                  marginRight: '0.5rem',
                }}
              />
            )}
            {isAudiobook && (
              <Icon
                name="audiobook"
                style={{
                  borderRadius: '1rem',
                  backgroundColor: '#B6BFBF26',
                  padding: '0.25rem',
                  width: '36px',
                  height: '36px',
                  marginRight: '0.5rem',
                }}
              />
            )}
            {isPlus && !userVip && (
              <Icon name="bookshelfPlusNew" style={{ width: '123px', marginRight: '1rem' }} />
            )}
            {(isPlus || isVip) && userVip && (
              <Icon
                name="digitalVipNew"
                style={{
                  width: '123px',
                }}
              />
            )}

            <Box isColumn alignItems="start" className="MediaCardLinkContainer">
              <Text className="MediaCardLink" as="a" target="_blank" href={dbcomUrl}>
                <Box as="span" alignItems="center">
                  <Text as="span">View on Deseret Book</Text>
                  <Icon name="redirect" style={{ marginLeft: '0.5rem' }} />
                </Box>
              </Text>
            </Box>
          </Box>
          <AttributesCard media={media} isEbook={isEbook} isAudiobook={isAudiobook} />
        </Box>
      </Box>
    </PageLayout>
  )
}

LandingPage.defaults = {
  match: {
    params: {
      id: '',
      sku: '',
    },
  },
}

LandingPage.propTypes = {
  match: propTypes.shape({
    params: propTypes.shape({
      id: propTypes.string,
      sku: propTypes.string,
    }),
  }).isRequired,
}

export default LandingPage
